import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useContext, useEffect } from 'react';
import { Alert, Button, Col, Container, Form, Row, Spinner } from 'react-bootstrap';
import { useForm } from 'react-hook-form';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import { z } from 'zod';

import { InvitationService, UserRegistrationWithInvitation } from '../api';
import { FormInputMaskField } from '../app/components/FormInputMaskField';
import { FormTextField } from '../app/components/FormTextField';
import { PasswordField } from '../app/components/PasswordField';
import { FormSubmitContext } from '../app/contexts/FormSubmitContext';
import { SessionContext } from '../app/contexts/SessionContext';
import { useDocumentTitle } from '../app/hooks/use-document-title';
import { invitationQueryKeys } from '../app/query-keys-factories/invitations';
import { handleApiError } from '../app/utils/errorhandling';
import { setFormErrorsFromApiErrors } from '../utils/forms';
import { strongPasswordZodSchema } from '../utils/security';

const validationSchema = z
  .object({
    email: z.string().min(1, { message: 'Email is required' }).email({
      message: "That email doesn't look quite right.",
    }),
    first_name: z.string().min(1, { message: 'First Name is required' }),
    last_name: z.string().min(1, { message: 'Last Name is required' }),
    phone_number: z.string().min(1, { message: 'Phone Number is required' }),
    title: z.string().min(1, { message: 'Title is required' }),
    password1: strongPasswordZodSchema,
    password2: z.string().min(1, { message: 'Confirm Password is required' }),
  })
  .refine(({ password1, password2 }) => password1 === password2, {
    path: ['password2'],
    message: "Your passwords don't match.",
  });

export const InvitationRegisterPage = () => {
  useDocumentTitle('Invitation for registration');
  const { setSessionExists } = useContext(SessionContext);
  const { invitationId } = useParams();
  const navigate = useNavigate();
  const { isSubmitEnabled } = useContext(FormSubmitContext);
  const {
    handleSubmit,
    formState: { errors },
    register,
    clearErrors,
    setValue,
    setError,
  } = useForm({
    resolver: zodResolver(validationSchema),
  });

  const queryInvitation = useQuery({
    queryKey: invitationQueryKeys.detailFromToken(invitationId!),
    queryFn: async () => {
      return await InvitationService.invitationFromTokenRetrieve({ token: invitationId! });
    },
  });

  useEffect(() => {
    if (queryInvitation.data?.email) {
      setValue('email', queryInvitation.data?.email);
    }
  }, [queryInvitation.data?.email]);

  const handleSubmitClick = () => {
    clearErrors();
  };

  const getOrganizationName = () => {
    if (queryInvitation.isLoading) return '...';
    if (queryInvitation.isError) return '';
    return queryInvitation.data?.healthcare_entity_name || 'Sendit';
  };

  const mutationRegisterRepWithInvitation = useMutation({
    mutationFn: async (data: UserRegistrationWithInvitation) => {
      return await InvitationService.invitationRegisterCreate({ requestBody: data });
    },
    onSuccess: () => {
      // Establish the session after successful registration
      setSessionExists();
      navigate('/app');
    },
  });

  // Callback for registration form submission
  const onSubmit = async ({ first_name, last_name, phone_number, ...data }) => {
    try {
      await mutationRegisterRepWithInvitation.mutateAsync({
        ...data,
        invitation_token: invitationId,
        first_name: first_name,
        last_name: last_name,
        phone_number: phone_number,
      } as UserRegistrationWithInvitation);
      toast.success('Your account was created successfully! Welcome to Sendit Gigs.');
    } catch (errors) {
      setFormErrorsFromApiErrors(errors, setError);
      handleApiError(errors, { messagePrefix: 'An error occurred while creating your account' });
    }
  };

  // Registration page
  return (
    <Container className="px-4 py-3" fluid>
      <Container>
        {queryInvitation.isLoading && (
          <Row className="my-3">
            <Col xs={12} className="d-flex align-items-center justify-content-center">
              <Spinner
                aria-label="loading indicator"
                animation="grow"
                className="loading-spinner"
                variant="primary"
              />
            </Col>
          </Row>
        )}
        {queryInvitation.isError ? (
          <Row className="row-cols-auto justify-content-center">
            <Col>
              <Alert variant="danger" show={queryInvitation.isError}>
                <Alert.Heading>Invalid invitation</Alert.Heading>
                <p>Please contact your Administrator to receive a new one.</p>
              </Alert>
            </Col>
          </Row>
        ) : (
          <Row className="d-flex justify-content-center align-items-center">
            <Col className="mb-5" lg={8} md={12}>
              <h1 className="fw-semibold fs-1 text-center text-color-primary-dark">
                Join {getOrganizationName()}
              </h1>
              <div className="text-center fs-5">
                Please complete the form below to register your account.
              </div>
              <Row className="d-flex justify-content-center align-items-center">
                <Col md={12}>
                  <hr className="my-5=2" />
                </Col>
              </Row>
              <Row className="d-flex justify-content-center align-items-center mb-3">
                <Col lg={10} sm={11}>
                  <Form noValidate onSubmit={handleSubmit(onSubmit)} id="register-form">
                    {errors?.root?.non_field_errors && (
                      <Alert variant="danger">{errors.root.non_field_errors.message}</Alert>
                    )}
                    {/* readOnly rather than disabled: the invited address is fixed, but a
                        disabled input is skipped by autofill (so the password gets saved
                        with no username), is dropped from form submission, and is passed
                        over by screen readers. readOnly keeps all three working — the
                        background matches Bootstrap's disabled input, which only styles
                        :disabled, so the field still reads as non-editable. */}
                    <FormTextField
                      label="Email"
                      name="email"
                      errors={errors}
                      register={register}
                      readOnly
                      autoComplete="username"
                      style={{ backgroundColor: 'var(--bs-secondary-bg)' }}
                      required
                    />
                    <FormTextField
                      label="First Name"
                      name="first_name"
                      errors={errors}
                      register={register}
                      required
                    />
                    <FormTextField
                      label="Last Name"
                      name="last_name"
                      errors={errors}
                      register={register}
                      required
                    />
                    <FormInputMaskField
                      label="Phone Number"
                      name="phone_number"
                      errors={errors}
                      register={register}
                      required
                      mask="(___) ___-____"
                      replacement={{ _: /\d/ }}
                      placeholder="(123) 456-7890"
                    />
                    <FormTextField
                      label="Title"
                      name="title"
                      errors={errors}
                      register={register}
                      required
                    />
                    <PasswordField
                      name="password1"
                      label="Password"
                      placeholder="Password"
                      errors={errors}
                      register={register}
                      autoComplete="new-password"
                      required
                    />
                    <PasswordField
                      name="password2"
                      label="Confirm Password"
                      placeholder="Confirm Password"
                      errors={errors}
                      register={register}
                      autoComplete="new-password"
                      required
                    />
                    <div className="d-grid py-3">
                      <Button
                        className="fs-5 py-3 rounded-3 btn-drop-shadow text-uppercase"
                        type="submit"
                        variant="primary"
                        onClick={() => handleSubmitClick()}
                        disabled={mutationRegisterRepWithInvitation.isLoading || !isSubmitEnabled}
                      >
                        Create Account
                      </Button>
                    </div>
                  </Form>
                </Col>
              </Row>
            </Col>
          </Row>
        )}
      </Container>
    </Container>
  );
};
